import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Modale } from '../ui/Modale';
import { BoutonPrincipal, BoutonSecondaire, Chargement, MessageErreur, Pastille, PastilleSite } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { useAuth } from '../../auth/useAuth';
import { ErreurApi } from '../../lib/api';
import {
  useReponseQuestionnaire,
  useTraiterAlerteQualite,
  type CategorieNps,
  type QuestionRepondue,
} from '../../lib/useQuestionnaires';

const TON_CATEGORIE: Record<CategorieNps, string> = {
  PROMOTEUR: 'success',
  PASSIF: 'neutral',
  DETRACTEUR: 'warning',
};

function dateFrancaise(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

function Reponse({ question, seuil }: { question: QuestionRepondue; seuil: number }): JSX.Element {
  if (question.type === 'NOTE' || question.type === 'NPS') {
    if (question.note === null) return <span className="text-sm text-codialis-gray">Sans réponse</span>;
    return (
      <span className="flex flex-wrap items-center gap-2">
        <span className={`text-sm font-bold ${question.basse ? 'text-red-600' : 'text-codialis-navy'}`}>
          {question.note}/{question.type === 'NPS' ? 10 : 5}
        </span>
        {question.basse && <Pastille texte={`Sous ${seuil}/5`} ton="danger" />}
        {question.categorie && question.libelleCategorie && (
          <Pastille texte={question.libelleCategorie} ton={TON_CATEGORIE[question.categorie]} />
        )}
      </span>
    );
  }
  if (question.type === 'TEXTE') {
    return question.texte ? (
      <p className="whitespace-pre-line break-words text-sm italic text-codialis-navy">« {question.texte} »</p>
    ) : (
      <span className="text-sm text-codialis-gray">Sans réponse</span>
    );
  }
  return question.choix.length > 0 ? (
    <span className="break-words text-sm text-codialis-navy">{question.choix.join(' · ')}</span>
  ) : (
    <span className="text-sm text-codialis-gray">Sans réponse</span>
  );
}

/**
 * Un questionnaire répondu, en entier (TF-434) : la cible du lien « Voir la
 * réponse » de l'alerte de note basse — e-mail, notification, carte « Alertes
 * qualité ». Chaque ouverture est tracée par l'API ; l'alerte se marque traitée
 * ici quand le lecteur en a le droit.
 */
export function ModaleReponse({ id, onFermer }: { id: string; onFermer: () => void }): JSX.Element {
  const { data, isLoading, error } = useReponseQuestionnaire(id);
  const traiter = useTraiterAlerteQualite();
  const notifier = useToast();
  const { peutAcceder } = useAuth();

  return (
    <Modale
      titre={data ? data.libelleType : 'Réponse au questionnaire'}
      sousTitre={data ? `« ${data.titre} »` : undefined}
      ouverte
      onFermer={onFermer}
      largeur="max-w-3xl"
    >
      <div className="space-y-5 p-4 sm:p-6">
        {isLoading && <Chargement />}
        {error instanceof ErreurApi && <MessageErreur message={error.message} />}

        {data && (
          <>
            <dl className="grid grid-cols-1 gap-3 rounded-2xl bg-codialis-sand p-4 text-sm sm:grid-cols-2">
              <div className="min-w-0">
                <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">Apprenant</dt>
                <dd className="flex flex-wrap items-center gap-2 font-semibold text-codialis-navy">
                  {peutAcceder('adm-dossier') ? (
                    <Link to={`/adm-dossier/${data.apprenant.id}`} className="text-codialis-sky hover:underline">
                      {data.apprenant.nom}
                    </Link>
                  ) : (
                    data.apprenant.nom
                  )}
                  <PastilleSite site={data.apprenant.siteCode} />
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">Formation</dt>
                <dd className="break-words text-codialis-navy">
                  {data.apprenant.formation ?? 'Sans formation rattachée'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">Formateur référent</dt>
                <dd className="text-codialis-navy">{data.apprenant.formateur ?? 'Sans formateur référent'}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-[11px] font-bold uppercase tracking-wide text-codialis-gray">Envoyé · répondu</dt>
                <dd className="text-codialis-navy">
                  le {dateFrancaise(data.envoyeLe)} ·{' '}
                  {data.repondLe ? `le ${dateFrancaise(data.repondLe)}` : 'pas encore'}
                </dd>
              </div>
            </dl>

            {data.alerte && (
              <div
                className={`flex flex-col gap-3 rounded-2xl border p-4 sm:flex-row sm:items-center ${
                  data.alerte.statut === 'OUVERTE' ? 'border-red-200 bg-red-50' : 'border-gray-200 bg-white'
                }`}
              >
                <div className="flex min-w-0 flex-1 items-start gap-2 text-sm text-codialis-navy">
                  {data.alerte.statut === 'OUVERTE' ? (
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden />
                  ) : (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-codialis-mint" aria-hidden />
                  )}
                  <span className="min-w-0">
                    {data.notesBasses > 1 ? `${data.notesBasses} critères notés` : 'Un critère noté'} sous{' '}
                    {data.seuilNoteBasse}/5 · alerte
                    du {dateFrancaise(data.alerte.declencheeLe)}
                    {data.alerte.statut === 'OUVERTE'
                      ? ', à traiter'
                      : `, ${data.alerte.statut === 'TRAITEE' ? 'traitée' : 'ignorée'}${
                          data.alerte.traiteeLe ? ` le ${dateFrancaise(data.alerte.traiteeLe)}` : ''
                        }`}
                  </span>
                </div>
                {data.alerte.statut === 'OUVERTE' && data.peutStatuer && (
                  <BoutonPrincipal
                    onClick={() =>
                      traiter.mutate(data.alerte?.id ?? '', {
                        onSuccess: () => notifier('Alerte marquée traitée.'),
                      })
                    }
                    disabled={traiter.isPending}
                  >
                    {traiter.isPending ? 'Enregistrement…' : 'Marquer traitée'}
                  </BoutonPrincipal>
                )}
              </div>
            )}
            {traiter.error instanceof ErreurApi && <MessageErreur message={traiter.error.message} />}

            <ol className="divide-y divide-gray-100 rounded-2xl border border-gray-200">
              {data.questions.map((question, rang) => (
                <li key={question.id} className={`space-y-1.5 p-4 ${question.basse ? 'bg-red-50/60' : ''}`}>
                  <div className="text-sm font-semibold text-codialis-navy">
                    <span className="mr-2 text-codialis-gray">{rang + 1}.</span>
                    {question.libelle}
                  </div>
                  <Reponse question={question} seuil={data.seuilNoteBasse} />
                </li>
              ))}
            </ol>
          </>
        )}
      </div>
      <div className="flex border-t border-gray-100 p-4 sm:px-6">
        <BoutonSecondaire onClick={onFermer}>Fermer</BoutonSecondaire>
      </div>
    </Modale>
  );
}
